import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import IconChevronRight from "../../assets/chevron-right.png";
import IconHome from "../../assets/home.svg";
import IconStar from "../../assets/star.svg";
import IconCalendarDays from "../../assets/calendar-days.svg";
import IconBus from "../../assets/bus.svg";
import IconCheck from "../../assets/check.svg";
import haliTestImage from "../../assets/hali-test.png";
import Footer from "../../components/Footer";
import homeIcon from "../../assets/home.png";
import deliverIcon from "../../assets/deliver.png";
import pd2Icon from "../../assets/pd-2.png";
import { FiChevronDown } from "react-icons/fi";
import useProductGeneralDetail from "../../hooks/useProductGeneralDetail";
import useProductDealers from "../../hooks/useProductDealers";
import { authService } from "../../services/auth";
import { useSelectedLocation } from "../../context/LocationContext";
import { useCart } from "../../hooks/useCart";

const ProductDetail = () => {
  const { id: productId } = useParams();
  const isAuthenticated = authService.isAuthenticated();
  const currentUser = authService.getCurrentUser();
  const {
    productDetail,
    loading: detailLoading,
    error: detailError,
    fetchProductById,
  } = useProductGeneralDetail();
  const {
    dealerPrices,
    loading: dealersLoading,
    error: dealersError,
    fetchDealerPrices,
  } = useProductDealers();
  const { selectedLocation, isLoadingLocation } = useSelectedLocation();
  const { addItemToCart, isAddingItem, addItemError } = useCart();
  const [activeTab, setActiveTab] = useState("description");
  const [selectedDealerInfo, setSelectedDealerInfo] = useState(null);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [showAddedAlert, setShowAddedAlert] = useState(false);
  const [addedProductName, setAddedProductName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchProductById(productId);
  }, [productId, fetchProductById]);

  useEffect(() => {
    const districtIdToFetch = selectedLocation?.districtId;

    if (productId && districtIdToFetch) {
      console.log(
        "ProductDetail: Context'ten gelen ilçe ID (",
        districtIdToFetch,
        ") ile bayi fiyatları getiriliyor..."
      );
      fetchDealerPrices(productId, districtIdToFetch);
    } else {
      console.log(
        "ProductDetail: Bayi fiyatlarını getirmek için ilçe ID eksik (Context: ",
        selectedLocation,
        ")."
      );
    }
  }, [productId, selectedLocation, fetchDealerPrices]);

  useEffect(() => {
    setIsAddedToCart(false);
    setSelectedDealerInfo(null);
  }, [dealerPrices]);

  const pageTitle =
    productDetail?.name || dealerPrices?.[0]?.product_name || "Ürün Detayı";
  const categoryName =
    productDetail?.category?.name ||
    dealerPrices?.[0]?.category_name ||
    "Kategori";
  const categoryIdForLink =
    productDetail?.category_id || dealerPrices?.[0]?.category_id;
  const imageUrl = productDetail?.image_url;
  const productPrice = dealerPrices?.[0]?.price || "--";
  const providerName = dealerPrices?.[0]?.dealer_name || "Belirtilmemiş";
  const productDescription =
    productDetail?.description || "Açıklama bulunamadı.";
  const rating = productDetail?.rating || 4.7;
  const reviewCount = productDetail?.review_count || 275;

  const getImageUrl = (imagePath) => {
    if (!imagePath) return haliTestImage;
    return imagePath.startsWith("uploads")
      ? `https://api.yikafoni.com/${imagePath}`
      : imagePath;
  };
  const finalImageUrl = getImageUrl(imageUrl);

  const isLoading = detailLoading || isLoadingLocation;
  const hasError = detailError;

  const handleDealerSelect = (dealerInfo) => {
    setSelectedDealerInfo(dealerInfo);
  };

  const handleAddToCart = async () => {
    if (!selectedDealerInfo || !productId) {
      alert("Lütfen önce bir sağlayıcı seçin.");
      return;
    }

    setIsAddedToCart(false);

    const success = await addItemToCart(
      selectedDealerInfo.dealer_id,
      productId
    );

    if (success) {
      setAddedProductName(pageTitle || "Ürün");
      setShowAddedAlert(true);
      setTimeout(() => {
        setShowAddedAlert(false);
        navigate("/sepetim");
      }, 1500);
    } else {
      alert(`Hata: ${addItemError || "Ürün sepete eklenemedi."}`);
    }
  };

  const renderContent = () => {
    if (hasError && !productDetail) {
      return (
        <div className="text-center py-10 text-red-500">
          <p>Hata: {detailError}</p>
        </div>
      );
    }
    if (isLoading && !productDetail) {
      return (
        <div className="text-center py-10 text-gray-500">
          <p>Ürün detayları yükleniyor...</p>
        </div>
      );
    }
    if (!isLoading && !productDetail && !hasError) {
      return (
        <div className="text-center py-10 text-gray-500">
          <p>Ürün bulunamadı.</p>
        </div>
      );
    }
    return (
      <>
        <div className="flex flex-col lg:flex-row gap-8 xl:gap-12">
          <div className="lg:w-2/5 flex justify-center">
            <img
              src={finalImageUrl}
              alt={pageTitle}
              className="w-full max-w-xs md:max-w-sm lg:max-w-none h-auto rounded-lg object-cover shadow-md"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = haliTestImage;
              }}
            />
          </div>

          <div className="flex flex-col space-y-4 lg:flex-1">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">{rating.toFixed(1)}</span>
              <div className="flex items-center">
                {[...Array(Math.round(rating || 0))].map((_, i) => (
                  <img
                    key={i}
                    src={IconStar}
                    alt="Star"
                    className="w-4 h-4 text-yellow-400"
                  />
                ))}
                {[...Array(5 - Math.round(rating || 0))].map((_, i) => (
                  <img
                    key={i}
                    src={IconStar}
                    alt="Star Outline"
                    className="w-4 h-4 text-gray-300"
                  />
                ))}
              </div>
              <span
                className="text-indigo-600 text-sm font-semibold cursor-pointer hover:underline"
                onClick={() => setActiveTab("reviews")}
              >
                {reviewCount} Değerlendirme
              </span>
            </div>
            <div className="text-xl md:text-2xl font-semibold text-gray-900">
              {productPrice} TL
            </div>
            <div className="text-sm text-gray-500">
              Sağlayıcı: {providerName}
            </div>

            <div className="pt-4">
              <h3 className="text-base font-semibold text-gray-800 mb-3">
                Sağlayıcı Seçin:
              </h3>
              {isLoadingLocation && (
                <p className="text-sm text-gray-500">Konum yükleniyor...</p>
              )}
              {!isLoadingLocation && !selectedLocation && (
                <p className="text-sm text-orange-600">
                  Lütfen header'dan konum seçin.
                </p>
              )}
              {dealersLoading && (
                <p className="text-sm text-gray-500">Bayiler yükleniyor...</p>
              )}
              {dealersError && (
                <p className="text-sm text-red-500">
                  Bayi fiyatları yüklenemedi.
                </p>
              )}
              {((selectedLocation && !isAuthenticated) ||
                (isAuthenticated && currentUser?.district_id)) &&
                !dealersLoading &&
                !dealersError &&
                dealerPrices.length === 0 && (
                  <p className="text-sm text-gray-500">
                    (Seçili konumda bu ürünü sunan bayi bulunamadı)
                  </p>
                )}
              {dealerPrices && dealerPrices.length > 0 && (
                <div className="flex flex-col space-y-3 border-t border-gray-100 pt-3 mt-2">
                  {dealerPrices.map((item) => (
                    <div
                      key={item.dealer_id}
                      className={`border rounded-lg p-3 cursor-pointer transition-all duration-150 ${
                        selectedDealerInfo?.dealer_id === item.dealer_id
                          ? "border-indigo-600 ring-2 ring-indigo-200 bg-indigo-50"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                      onClick={() => handleDealerSelect(item)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {item.dealer_name || "Bayi Adı Yok"}
                        </span>
                        <span className="text-sm font-semibold text-indigo-700">
                          {item.price} TL
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col w-full lg:w-1/3 h-fit p-4 border border-gray-200 rounded-lg lg:ml-auto shadow-sm">
            {selectedLocation && (
              <div className="mb-4 pb-4 border-b border-gray-100">
                <div className="text-sm text-gray-900 font-semibold mb-1">
                  Teslimat Bölgesi:
                </div>
                <div className="text-sm text-gray-700">
                  {selectedLocation.cityName || "Şehir Yok"},{" "}
                  {selectedLocation.districtName || "İlçe Yok"}
                </div>
              </div>
            )}
            {isLoadingLocation && (
              <p className="text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                Konum yükleniyor...
              </p>
            )}
            {!isLoadingLocation && !selectedLocation && (
              <p className="text-xs text-orange-600 mb-4 pb-4 border-b border-gray-100">
                Lütfen header'dan konum seçin.
              </p>
            )}

            <div className="flex items-center mb-4">
              <img
                src={IconCheck}
                alt="Check"
                className="w-4 h-4 mr-2 text-green-500"
              />
              <span className="text-sm text-gray-500">
                {!selectedLocation
                  ? "Konum seçmelisiniz"
                  : dealersLoading
                  ? "Kontrol ediliyor..."
                  : dealerPrices && dealerPrices.length > 0
                  ? "Seçili konumda hizmet mevcut"
                  : "Seçili konumda hizmet yok"}
              </span>
            </div>

            <div className="mb-4">
              <label
                htmlFor="haliAdedi"
                className="block text-sm text-gray-900 font-semibold mb-2"
              >
                Adet:
              </label>
              <select
                id="haliAdedi"
                name="haliAdedi"
                className="w-full border border-gray-300 rounded-md p-2 text-sm text-gray-900"
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} adet
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              {!isAddedToCart ? (
                <button
                  className={`w-full text-white text-sm font-medium rounded-md py-3 text-center transition-colors flex items-center justify-center ${
                    !selectedDealerInfo || !selectedLocation || isAddingItem
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                  disabled={
                    !selectedDealerInfo || !selectedLocation || isAddingItem
                  }
                  onClick={handleAddToCart}
                >
                  {isAddingItem ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white">
                        {/* Spinner */}
                      </svg>
                      Ekleniyor...
                    </>
                  ) : !selectedLocation ? (
                    "Önce Konum Seçin"
                  ) : selectedDealerInfo ? (
                    `${selectedDealerInfo.price} TL - Sepete Ekle`
                  ) : (
                    "Önce Sağlayıcı Seçin"
                  )}
                </button>
              ) : (
                <div className="text-center text-green-600 font-medium text-sm py-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline w-5 h-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Sepete Eklendi!
                </div>
              )}
            </div>
            <span className="text-xs text-gray-500 pt-3 block text-center">
              Halı detayları bir sonraki sayfada alınacaktır.
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          <div className="flex flex-col items-center text-center border border-gray-200 rounded-lg p-6">
            <div className="w-12 h-12 mb-4">
              <img
                src={homeIcon}
                alt="Home Icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm font-semibold text-[#111827] mb-1">
              Kapınızdan alınır
            </h3>
            <p className="text-sm text-[#6B7280]">
              Seçtiğiniz tarihte kapınızdan alınır
            </p>
          </div>

          <div className="flex flex-col items-center text-center border border-gray-200 rounded-lg p-6">
            <div className="w-12 h-12 mb-4">
              <img
                src={deliverIcon}
                alt="Deliver Icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm font-semibold text-[#111827] mb-1">
              3 iş günü içinde teslim
            </h3>
            <p className="text-sm text-[#6B7280]">
              3 iş günü içerisinde kapınıza teslim edilir
            </p>
          </div>

          <div className="flex flex-col items-center text-center border border-gray-200 rounded-lg p-6">
            <div className="w-12 h-12 mb-4">
              <img
                src={pd2Icon}
                alt="Insurance Icon"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-sm font-semibold text-[#111827] mb-1">
              Sigorta güvencesi
            </h3>
            <p className="text-sm text-[#6B7280]">
              Halılarınızı sigortalıyoruz
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center">
          <div className="flex justify-center items-center gap-7">
            <div
              className={`p-7 flex items-center cursor-pointer ${
                activeTab === "description" ? "border-b-2 border-[#4F46E5]" : ""
              }`}
              onClick={() => setActiveTab("description")}
            >
              <div
                className={`text-center ${
                  activeTab === "description"
                    ? "text-[#4F46E5]"
                    : "text-neutral-500"
                } text-base font-bold`}
              >
                Açıklama
              </div>
            </div>
            <div
              className={`p-7 flex items-center gap-1.5 cursor-pointer ${
                activeTab === "reviews" ? "border-b-2 border-[#4F46E5]" : ""
              }`}
              onClick={() => setActiveTab("reviews")}
            >
              <div
                className={`text-center ${
                  activeTab === "reviews"
                    ? "text-[#4F46E5]"
                    : "text-neutral-500"
                } text-base font-bold`}
              >
                Yorumlar
              </div>
              <div className="text-center text-[#4F46E5] text-sm">(275)</div>
            </div>
          </div>
          <div className="w-full h-px bg-gray-200"></div>
        </div>

        <div className="mt-8">
          {activeTab === "description" && (
            <div className="pt-7 pb-14 bg-white text-gray-600 text-sm md:text-base leading-relaxed">
              {productDescription}
            </div>
          )}
          {activeTab === "reviews" && (
            <div className="pt-7 pb-14 bg-white">
              <div className="flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 order-1 sm:order-1">
                    Müşteri Yorumları{" "}
                    <span className="text-base font-normal text-gray-500 ml-2">
                      (275 Değerlendirme)
                    </span>
                  </h2>
                  <div className="flex items-center gap-4 order-2 sm:order-2 self-end sm:self-center">
                    <button className="p-2 border border-gray-200 rounded-lg">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 7H21"
                          stroke="#111827"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M6 12H18"
                          stroke="#111827"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M10 17H14"
                          stroke="#111827"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <select className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-900">
                      <option>En Son</option>
                      <option>En Yüksek Puan</option>
                      <option>En Düşük Puan</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="p-6 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(index % 2 === 0 ? 5 : 4)].map((_, i) => (
                          <img
                            key={i}
                            src={IconStar}
                            alt="Star"
                            className="w-4 h-4 text-yellow-400"
                          />
                        ))}
                      </div>
                      <h3 className="text-base font-semibold text-gray-800 mb-2">
                        Kullanıcı Adı {index + 1}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-4">
                        Yorum metni buraya gelecek...
                      </p>
                      <span className="text-xs text-gray-400">Tarih</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center mt-8">
                  <button className="px-4 py-2 text-sm text-[#111827] bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                    Daha Fazlasını Göster
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 order-2 sm:order-1">
              {isLoading ? "Yükleniyor..." : pageTitle}
            </h1>
            <div className="flex items-center gap-2 text-xs sm:text-sm order-1 sm:order-2">
              <img src={IconHome} alt="Home" className="w-4 h-4" />
              <Link to="/" className="text-indigo-600 hover:underline">
                Yıkafoni
              </Link>
              <span className="text-gray-400">/</span>
              {categoryIdForLink ? (
                <Link
                  to={`/category/${categoryIdForLink}`}
                  className="text-indigo-600 hover:underline"
                >
                  {categoryName}
                </Link>
              ) : (
                <span className="text-gray-500">{categoryName}</span>
              )}
              <span className="text-gray-400">/</span>
              <span className="text-gray-500 truncate max-w-[150px]">
                {pageTitle}
              </span>
            </div>
          </div>

          {renderContent()}
        </div>
      </div>

      <div className="mb-48"></div>

      <Footer />
    </>
  );
};

export default ProductDetail;
